import React, { useState, useEffect, useCallback, useMemo } from 'react';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
    Box,
    Typography,
    Stack,
    Paper,
    Divider,
} from '@mui/material';

import { VerifyResult } from '../../features/oidcSlice';
import Link from "@mui/material/Link";
import { useAuth } from 'react-oidc-context';

import { useCustomAuth } from '../../app/auth/useCustomAuth';
import { baseName } from '../..';
import { useNavigate } from 'react-router-dom';
import { SmsConsent } from '../profile/SmsConsent';
import { SmsPhoneValidation } from '../profile/SmsPhoneValidation';
import { formatPhone } from '../../app/auth/phoneEntryUtils';

export const updateUrlParams = ({
    url,
    removeParams = [],
    addParams = [],
}: {
    url: string;
    removeParams?: string[];
    addParams?: { key: string; value?: string }[];
}): string => {
    const u = new URL(url, 'http://_'); // dummy base for parsing

    // remove params
    removeParams.forEach((param) => u.searchParams.delete(param));

    // add / replace params
    addParams.forEach(({ key, value }) => {
        if (value !== undefined) {
            u.searchParams.set(key, value);
        } else {
            // undefined value → remove if exists
            u.searchParams.delete(key);
        }
    });

    // return relative URL
    return `${u.pathname}${u.search}${u.hash}`;
};

export const SmsLogin: React.FC<{ navigateTo?: string, lockedPhoneNumber?: string, showExistingAccountNotice?: boolean }> = ({ navigateTo, lockedPhoneNumber, showExistingAccountNotice = true }) => {
    // Two independent acceptances, deliberately NOT bundled (carriers expect a
    // single-purpose messaging consent):
    //   consentChecked — OPTIONAL opt-in to ongoing SMS from Polygon Health.
    //   termsAccepted  — REQUIRED; the only gate on sending the code, since the code
    //                    itself is the login credential covered by the Terms.
    const [consentChecked, setConsentChecked] = useState<boolean>(false);
    const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
    const navigate = useNavigate();
    const auth = useAuth();
    const { login } = useCustomAuth();

    // Clear stale auth state once on mount (not every render — would trigger
    // re-renders that destabilize the SmsPhoneValidation child).
    useEffect(() => {
        auth.clearStaleState();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // All three props passed to SmsPhoneValidation MUST be referentially stable
    // across renders. The child's useEffect dep arrays include `value` and
    // `onChange`; inline objects/arrows would create new refs every render and
    // produce a setState loop (Maximum update depth exceeded).
    const handleVerifySuccess = useCallback((verifyResult: VerifyResult) => {
        console.log('Verification succeeded — backend redirect expected.');
        console.info(verifyResult);
        login(verifyResult);
        navigate(navigateTo ? updateUrlParams({ url: navigateTo, removeParams: ["t"] }) || "/" : '/');
    }, [login, navigate, navigateTo]);

    const handleNoOpChange = useCallback(() => {
        /* no-op: login flow only cares about verify success */
    }, []);

    const smsValue = useMemo(() => ({
        phone: lockedPhoneNumber ? formatPhone(lockedPhoneNumber.replace(/^\+1/, ''), false) : '',
        verificationComplete: false,
        consentChecked,
    }), [lockedPhoneNumber, consentChecked]);

    return (
        <Box>
            <Stack sx={{ width: '100%', gap: { xs: 1, sm: 2 } }}>
                <Typography variant="body2" sx={{ width: '100%', wordBreak: 'break-word' }} mb={1} textAlign="center">
                    Enter your mobile number and we'll text you a 6-digit verification code.
                </Typography>
                {showExistingAccountNotice && <ExistingAccountNotice />}

                <SmsPhoneValidation
                    value={smsValue}
                    disabled={!!lockedPhoneNumber}
                    showVerificationButtons={true}
                    suppressConsent={true}
                    suppressNoConsentWarning={true}
                    suppressConsentGate={true}
                    sendDisabled={!termsAccepted}
                    defaultHelperText="We'll text you a 6-digit code. Standard message rates apply."
                    onChange={handleNoOpChange}
                    onVerifySuccess={handleVerifySuccess}
                />

                <Divider />

                <SmsConsent
                    consentChecked={consentChecked}
                    onConsentChange={setConsentChecked}
                    showTermsAcceptance={true}
                    termsAccepted={termsAccepted}
                    onTermsAcceptedChange={setTermsAccepted}
                />

                <NoTextsAlternative navigateTo={navigateTo} />
            </Stack>
        </Box>
    );
};





export const ExistingAccountNotice: React.FC<{ navigateTo?: string }> = ({ navigateTo }) => {
    const auth = useAuth();
    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 1, sm: 2 },
                mt: { xs: 0, sm: 2 },
                borderRadius: 2,
                bgcolor: "rgba(33, 150, 243, 0.1)", // light blue background
                display: "flex",
                alignItems: "flex-start",
            }}
        >
            <InfoOutlinedIcon
                color="primary"
                sx={{ fontSize: 32, mr: 1, mt: "2px", flexShrink: 0 }}
            />
            {/* <WarningAmberIcon
        color="warning"
        sx={{
          fontSize: 32,       // bigger icon
          mr: 1.5,
          mt: "2px",
          flexShrink: 0,
        }} 
      />*/}
            <Typography variant="subtitle2">
                Already have an account? Link your phone by signing in with your <Link component="button" variant="body2"
                    onClick={(event) => {
                        // 1. Prevent the default link behavior (if an href was set)
                        event.preventDefault();
                        // 2. Execute the auth sign-in redirect logic directly in the handler
                        auth.signinRedirect({
                            state: { baseName, ...navigateTo ? { route: updateUrlParams({ url: navigateTo, removeParams: ["t"] }) } : {} },
                        });
                    }}
                >email or social account
                </Link>.
            </Typography>
        </Paper>
    );
};

/** The opt-OUT path for this screen. There is no "I don't consent" checkbox here —
 *  the SMS code IS the credential, so declining messaging means using a different
 *  sign-in method rather than continuing without consent. */
export const NoTextsAlternative: React.FC<{ navigateTo?: string }> = ({ navigateTo }) => {
    const auth = useAuth();
    const signIn = (event: React.SyntheticEvent) => {
        event.preventDefault();
        auth.signinRedirect({
            state: { baseName, ...navigateTo ? { route: updateUrlParams({ url: navigateTo, removeParams: ["t"] }) } : {} },
        });
    };
    return (
        <Stack direction="row" spacing={1} alignItems="flex-start">
            <CheckCircleOutlineIcon sx={{ fontSize: 18, mt: '2px', flexShrink: 0, color: 'success.main' }} />
            <Typography variant="caption" sx={{ lineHeight: 1.5 }}>
                Prefer no texts at all? Sign in with{" "}
                <Link component="button" variant="caption" onClick={signIn}>email</Link> or a{" "}
                <Link component="button" variant="caption" onClick={signIn}>social account</Link> instead
            </Typography>
        </Stack>
    );
};

export interface SmsConsentProps {
    consentChecked: boolean | undefined;
    onConsentChange: (checked: boolean) => void;
}


// export const SmsConsent: React.FC<SmsConsentProps> = ({
//     consentChecked,
//     onConsentChange
// }) => {
//     const handleConsentChange = (
//         event: ChangeEvent<HTMLInputElement>
//     ): void => {
//         onConsentChange(event.target.checked);
//     };

//     return (<>
//         {/* Checkbox + text row */}
//         <Box sx={{ display: "flex", alignItems: "flex-start" }}>
//             <Checkbox
//                 checked={consentChecked}
//                 onChange={handleConsentChange}
//                 color="primary"
//             />
//             {/* <Typography variant="body2"> */}
//             <Box>
//                 {smsDisclosure("By checking this box you agree ")}
//             {/* </Typography> */}
//             </Box>

//         </Box>
//     </>);
// };
